/**
 * Conectar um canal — o caminho guiado que o §5.2 da spec 04 pede, agora que existem canais para ligar.
 *
 * ## O que estava errado, e não era a falta de um botão
 *
 * O produto tinha **dois** canais que o cliente consegue ligar sozinho — Telegram e WhatsApp por
 * aparelho vinculado — e nenhum lugar onde ele descobrisse isso. O Telegram era um formulário no rodapé
 * da tela de Canais, abaixo da lista, sem nada dizendo que ele existia; o WhatsApp exigia um comando de
 * terminal numa máquina que não é do cliente. A tela de Canais respondia "o que já está ligado?" e
 * ninguém respondia **"o que dá para ligar?"**.
 *
 * ## Por que os canais que NÃO dá para ligar aparecem
 *
 * §2 da spec 04: *"na tela de administração, sumir com o controle faz o admin achar que o produto não
 * tem a função"*. Uma lista só com Telegram e WhatsApp-por-QR diria, sem dizer, que o Hugi não fala
 * Instagram — quando a verdade é que a Meta não liberou o caminho. Cada um aparece com **o que falta e
 * de quem depende**, que é a mesma distinção que o checklist de ativação faz no Início: espera que não é
 * do cliente tem de ser dita, senão ele abre chamado sobre um passo que ninguém aqui pode acelerar.
 *
 * ## A ordem da lista é a do esforço, não a do catálogo
 *
 * Primeiro o que ele consegue terminar hoje. Uma lista em ordem de importância comercial começaria pelo
 * WhatsApp oficial — o canal que ele **não** consegue ligar —, e a primeira coisa que a tela diria a
 * quem acabou de assinar seria "não dá".
 */

import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { identificadorLegivel, useSessao, Ladrilho, MarcaHugi, type TomDoLadrilho } from "@hugi/ui";
import { CabecalhoDaTela, Motivo, PaginaInteira, SemPermissao, Selo, type Tom } from "@hugi/ui/kit";
import { ChevronRight, Facebook, Instagram, Mail, MessageCircle, Radio, Send, Smartphone } from "lucide-react";
import {
  cancelarPareamento,
  conectarTelegram,
  abrirIntencaoDeConexao,
  emitirLinkDePareamento,
  pedirPareamento,
  responderConfirmacao,
  useAgentes,
  useAguardandoConfirmacao,
  useAtivacao,
  usePareamento,
  type TelegramConectado,
} from "./dados.js";
import "./conectar.css";

type Disponibilidade = "pronto" | "depende_da_meta" | "nao_construido";

interface CanalDoCatalogo {
  readonly id: string;
  readonly nome: string;
  /** Uma linha: o que a pessoa ganha ligando isto. */
  readonly oQueE: string;
  readonly disponibilidade: Disponibilidade;
  /** Obrigatório quando não está pronto: o que falta, e de quem depende. */
  readonly porQue?: string;
}

/**
 * Os sete canais da ADR-0005, com o estado de HOJE.
 *
 * O rótulo do WhatsApp por QR **não é "não oficial"**. Esse é o nosso vocabulário interno, e para quem
 * está contratando ele soa como "clandestino" sem informar nada.
 *
 * Desde 2026-09-03 ele tem NOME PRÓPRIO: **API Hugi** (decisão do owner). O nome carrega a marca em
 * vez de descrever o meio de conectar — e sobrevive à troca de tecnologia, que já aconteceu uma vez
 * (Baileys → whatsmeow, ADR-0010) sem o cliente precisar saber.
 *
 * ⚠️ O NOME sozinho não faz ninguém escolher, e por isso a descrição continua dizendo o que ele é:
 * o número que a pessoa já usa, vinculado como um aparelho a mais. Nome curto na frente, o que
 * importa logo embaixo.
 */
const CANAIS: readonly CanalDoCatalogo[] = [
  { id: "whatsapp_qr", nome: "API Hugi", oQueE: "Seu WhatsApp, pelo QR.", disponibilidade: "pronto" },
  { id: "telegram", nome: "Telegram", oQueE: "Um bot da empresa.", disponibilidade: "pronto" },
  { id: "whatsapp_oficial", nome: "WhatsApp oficial", oQueE: "API da Meta, com selo.", disponibilidade: "pronto" },
  { id: "instagram", nome: "Instagram", oQueE: "Mensagens do perfil.", disponibilidade: "depende_da_meta" },
  { id: "facebook", nome: "Messenger", oQueE: "Conversas da página.", disponibilidade: "depende_da_meta" },
  { id: "sms", nome: "SMS", oQueE: "Texto, sem WhatsApp.", disponibilidade: "nao_construido" },
  { id: "email", nome: "E-mail", oQueE: "Na mesma caixa.", disponibilidade: "nao_construido" },
];


const SELO: Readonly<Record<Disponibilidade, { tom: Tom; rotulo: string }>> = {
  pronto: { tom: "sucesso", rotulo: "Disponível" },
  depende_da_meta: { tom: "neutro", rotulo: "Em breve" },
  nao_construido: { tom: "neutro", rotulo: "Em breve" },
};

const NOME_DO_PASSO: Readonly<Record<string, string>> = {
  whatsapp_qr: "API Hugi",
  whatsapp_oficial: "WhatsApp oficial",
  telegram: "Telegram",
};

const DICA_DA_TELA = "Por onde as conversas chegam. Pode ligar mais de um.";

/**
 * Conectar na família **Cadastro** da v2 (spec 22, 31/10).
 *
 * A escolha do canal é a primeira etapa: os ladrilhos numa região que rola por dentro (no computador cabem;
 * no celular rolavam a página). Cada canal escolhido é um passo no esqueleto do cadastro — trilha, cabeçalho,
 * painel cujo corpo rola e rodapé fixo com o "Escolher outro canal" à esquerda. As ações de cada passo
 * continuam DENTRO dele, porque mudam com o andamento (mostrar o código, gerar outro, abrir o cadastro da
 * Meta, confirmar o número): o `PaginaDeCadastro` do kit, com uma primária fixa, não descreve esse fluxo.
 */
/** `?canal=whatsapp_qr` (contrato das rotas de tela da spec 23): quem vem do Início abre direto no canal. */
function canalDaUrl(): string | null {
  if (typeof window === "undefined") return null;
  const c = new URLSearchParams(window.location.search).get("canal");
  return c && NOME_DO_PASSO[c] ? c : null;
}

export function Conectar() {
  const sessao = useSessao();
  const [escolhido, setEscolhidoNaTela] = useState<string | null>(canalDaUrl);
  const podeConectar = sessao.permissoes.includes("connect_channel");

  /*
    O PROFISSIONAL VAI DIRETO À API HUGI (spec 23, jornada 1). O plano dele é o número que a pessoa já usa, e a
    escolha entre sete ladrilhos era uma decisão a mais antes do primeiro passo. Quem é Profissional se sabe pela
    checklist de ativação (a dele tem o passo "conectar"); o oficial fica num link pequeno dentro do passo.
    Só na PRIMEIRA vez: quem clicou em "Escolher outro canal" fica na lista.
  */
  const ativacao = useAtivacao();
  const escolheu = useRef(escolhido !== null);
  const setEscolhido = (c: string | null) => {
    escolheu.current = true;
    setEscolhidoNaTela(c);
  };
  const profissional = (ativacao.data?.passos ?? []).some((p) => p.id === "conectar");
  useEffect(() => {
    if (profissional && !escolheu.current) {
      escolheu.current = true;
      setEscolhidoNaTela("whatsapp_qr");
    }
  }, [profissional]);

  if (!podeConectar) {
    /*
     * Sem a permissão a tela EXISTE e não conecta nada.
     *
     * O contrário — 403 ou rota ausente — faria o atendente concluir que o produto não conecta canal.
     * O que ele precisa saber é de quem pedir, e essa frase é a resposta.
     */
    return (
      <PaginaInteira rotulo="Conectar um canal" classe="hugi-conectar">
        <Cabecalho />
        <div className="v2-cartao">
          <SemPermissao>
            Conectar um canal exige a permissão <code>connect_channel</code>. Quem administra a sua empresa no Cert4All
            concede — e é a mesma pessoa que vê os canais já ligados.
          </SemPermissao>
        </div>
      </PaginaInteira>
    );
  }

  if (escolhido !== null && NOME_DO_PASSO[escolhido]) {
    return (
      <Passo canal={NOME_DO_PASSO[escolhido]!} aoVoltar={() => setEscolhido(null)}>
        {escolhido === "whatsapp_qr" ? (
          <WhatsAppPorQr aoPreferirOficial={() => setEscolhido("whatsapp_oficial")} />
        ) : escolhido === "whatsapp_oficial" ? (
          <WhatsAppOficial />
        ) : (
          <Telegram />
        )}
      </Passo>
    );
  }

  /*
    A escolha do canal usa a LARGURA TODA, em ladrilhos quase quadrados.

    Correção do owner (2026-09-03): *"faltou dar uma geral nessa tela, com os cards quadrados, ocupando a
    tela toda"*. A grade preenche por `auto-fill`: a 1440px cabem quatro, num notebook três, no celular um —
    sem ponto de quebra escrito à mão. E ela rola DENTRO da região, nunca a página (spec 22).
  */
  return (
    <PaginaInteira rotulo="Conectar um canal" classe="hugi-conectar">
      <Cabecalho />
      <div className="v2-elastico hugi-conectar-regiao" role="region" aria-label="Canais para conectar" tabIndex={0}>
        <ul className="hugi-conectar-grade">
          {/* Os que conectam agora primeiro (spec 23, J10); a ordem entre eles é a do catálogo. */}
          {[...CANAIS].sort((a, b) => Number(b.disponibilidade === "pronto") - Number(a.disponibilidade === "pronto")).map((c) => (
            <li key={c.id}>
              <CartaoDoCanal canal={c} aoEscolher={() => setEscolhido(c.id)} />
            </li>
          ))}
        </ul>
      </div>
    </PaginaInteira>
  );
}

function Cabecalho() {
  return <CabecalhoDaTela icone={<Radio size={18} />} titulo="Conectar um canal" dica={DICA_DA_TELA} />;
}

/**
 * Um passo do cadastro: trilha (Canais › Conectar um canal › o canal), cabeçalho, painel com o corpo que
 * rola e o rodapé fixo — o mesmo esqueleto e as mesmas classes do `PaginaDeCadastro` do kit.
 */
function Passo(props: { readonly canal: string; readonly aoVoltar: () => void; readonly children: ReactNode }) {
  const [, navegar] = useLocation();
  return (
    <section className="v2-pagina is-inteira v2-cadastro hugi-conectar" aria-label={`Conectar um canal: ${props.canal}`}>
      <nav className="v2-trilha" aria-label="Você está em">
        <span className="v2-trilha-nivel">
          <a href="/canais" onClick={(e) => { e.preventDefault(); navegar("/canais"); }}>Canais</a>
        </span>
        <span className="v2-trilha-nivel">
          <ChevronRight size={14} aria-hidden="true" />
          <a href="/canais/conectar" onClick={(e) => { e.preventDefault(); props.aoVoltar(); }}>Conectar um canal</a>
        </span>
        <span className="v2-trilha-nivel">
          <ChevronRight size={14} aria-hidden="true" />
          <span aria-current="page">{props.canal}</span>
        </span>
      </nav>
      <Cabecalho />
      <div className="v2-cartao v2-cadastro-painel">
        <div className="v2-cadastro-corpo">
          <div className="hugi-conectar-passo">{props.children}</div>
        </div>
        <footer className="v2-cadastro-rodape">
          <button type="button" className="v2-botao" onClick={props.aoVoltar}>← Escolher outro canal</button>
        </footer>
      </div>
    </section>
  );
}

/**
 * Conectar o WhatsApp oficial — o link hospedado pela Meta.
 *
 * ## Por que um link, e não uma janela dentro do painel
 *
 * Decisão do owner em 2026-09-01, à pergunta "o que tem menos atrito para o usuário". O Embedded
 * Signup com SDK abre pop-up e depende de cookie de terceiro; as duas coisas falham em celular e
 * falham **sem explicação útil**. E este fluxo é de celular na mão por natureza: para conectar o
 * número que já se usa, a pessoa precisa abrir o WhatsApp Business e colar um código.
 *
 * ## A tela avisa ANTES, porque o cliente vai sair daqui
 *
 * Quem clica é levado para um site da Meta. Não dizer isso antes faz a pessoa achar que errou o
 * caminho — e a metade que desiste nesse instante nunca vira canal conectado.
 */
function WhatsAppOficial() {
  const cliente = useQueryClient();
  const [link, setLink] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pedindo, setPedindo] = useState(false);
  const [respondendo, setRespondendo] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  const aguardando = useAguardandoConfirmacao(link !== null && !confirmado);
  const pendente = aguardando.data?.conexoes[0] ?? null;

  async function comecar() {
    setPedindo(true);
    setErro(null);
    const r = await abrirIntencaoDeConexao();
    setPedindo(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não consegui abrir o cadastro agora. Tente de novo.");
      return;
    }
    setLink(r.dados.url);
    // Aba NOVA, e o `noopener` não é enfeite: sem ele a página da Meta recebe uma referência à
    // nossa janela (`window.opener`) e pode navegá-la.
    window.open(r.dados.url, "_blank", "noopener,noreferrer");
  }

  async function responder(recusar: boolean) {
    if (!pendente) return;
    setRespondendo(true);
    const r = await responderConfirmacao(cliente, pendente.id, recusar);
    setRespondendo(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não consegui registrar a sua resposta. Tente de novo.");
      return;
    }
    if (recusar) {
      setLink(null);
      setErro(null);
    } else {
      setConfirmado(true);
    }
  }

  if (confirmado) {
    return (
      <>
        <div className="hugi-conectar-linha">
          <h2>WhatsApp conectado</h2>
          <Selo tom="sucesso">Recebendo</Selo>
        </div>
        <p className="hugi-conectar-fraco">
          As conversas deste número já chegam na caixa de entrada. Se você conectou o número que já usava, o WhatsApp
          Business continua funcionando normalmente no seu celular.
        </p>
        {/* O próximo passo, e não um fim de linha: conectado não é responder (spec 23). */}
        <ProximoPasso />
      </>
    );
  }

  /*
   * A PERGUNTA, e ela é a autoridade sobre de quem é o número.
   *
   * O `PARTNER_ADDED` da Meta não diz de quem é: a atribuição vem de uma heurística ("exatamente
   * uma intenção aberta"), que erra quando um tenant deixa intenção aberta sem concluir e outro
   * conclui sem intenção viva. Enquanto ninguém responde aqui, a conexão **não resolve tenant**.
   *
   * Por isso o número aparece INTEIRO e a recusa é tão visível quanto a confirmação.
   */
  if (pendente) {
    return (
      <>
        <h2>Encontramos um número. Ele é seu?</h2>
        <p className="hugi-conectar-numero">
          {pendente.numeroExibicao ? identificadorLegivel(pendente.numeroExibicao) : "(a Meta não informou o número)"}
        </p>
        <p className="hugi-conectar-fraco">
          A Meta avisa que um número foi conectado, mas não diz de qual empresa ele é. Confirme para as conversas dele
          começarem a chegar aqui — até lá, elas não entram na sua caixa.
        </p>
        <div className="hugi-conectar-botoes">
          <button type="button" className="v2-botao is-primario" onClick={() => void responder(false)} disabled={respondendo}>
            {respondendo ? "Registrando…" : "Sim, é o meu número"}
          </button>
          <button type="button" className="v2-botao" onClick={() => void responder(true)} disabled={respondendo}>
            Não é meu
          </button>
        </div>
        {erro !== null && <p className="v2-erro" role="alert">{erro}</p>}
      </>
    );
  }

  return (
    <>
      <h2>Conectar o WhatsApp da empresa</h2>

      <p>
        O cadastro acontece <strong>num site da Meta</strong>, que abre numa aba nova. Lá você pode cadastrar um número
        novo ou <strong>conectar o número que já usa no WhatsApp Business</strong> — nesse caso o aplicativo continua
        funcionando no seu celular, e as conversas passam a aparecer aqui também.
      </p>

      <p className="hugi-conectar-fraco">
        Se escolher o número que já usa, tenha o celular por perto: a Meta pede para colar um código dentro do WhatsApp
        Business.
      </p>

      <p className="hugi-conectar-fraco">
        No meio do caminho a Meta pede um <strong>portfólio empresarial</strong> — é a conta comercial dela, e é dela que
        essa exigência vem. Se você ainda não tiver um, dá para criar na hora, na própria tela: é só um nome para a sua
        empresa.
      </p>

      {link === null ? (
        <div className="hugi-conectar-botoes">
          <button type="button" className="v2-botao is-primario" onClick={() => void comecar()} disabled={pedindo}>
            {pedindo ? "Abrindo…" : "Abrir o cadastro da Meta"}
          </button>
        </div>
      ) : (
        <div>
          <p>
            Esperando você terminar na aba da Meta. Quando o cadastro for concluído, esta tela vai perguntar se o número
            é seu — pode deixá-la aberta.
          </p>
          <p className="hugi-conectar-fraco">
            Fechou a aba sem querer?{" "}
            <a href={link} target="_blank" rel="noopener noreferrer" className="v2-link">abrir de novo</a>.
          </p>
        </div>
      )}

      {erro !== null && <p className="v2-erro" role="alert">{erro}</p>}
    </>
  );
}

/** O ladrilho de cada canal — a mesma cor que ele terá no cartão de Canais depois de conectado. */
const TOM_DO_CANAL: Record<string, TomDoLadrilho> = {
  whatsapp_qr: "hugi",
  whatsapp_oficial: "wa",
  telegram: "tg",
  instagram: "sm",
  facebook: "tg",
  sms: "sm",
  email: "em",
};
const ICONE_DO_CANAL: Record<string, ReactNode> = {
  whatsapp_qr: <MarcaHugi />,
  whatsapp_oficial: <MessageCircle size={26} />,
  telegram: <Send size={26} />,
  instagram: <Instagram size={26} />,
  facebook: <Facebook size={26} />,
  sms: <Smartphone size={26} />,
  email: <Mail size={26} />,
};

function CartaoDoCanal(props: { readonly canal: CanalDoCatalogo; readonly aoEscolher: () => void }) {
  const { canal: c } = props;
  const pronto = c.disponibilidade === "pronto";
  const selo = SELO[c.disponibilidade];

  // Ícone, nome, uma frase, e o botão. O que ainda não dá diz só "Em breve" — explicação é ruído aqui.
  return (
    <article className={`v2-cartao hugi-conectar-ladrilho${pronto ? "" : " is-em-breve"}`}>
      <Ladrilho tom={TOM_DO_CANAL[c.id] ?? "neutro"} grande>
        {ICONE_DO_CANAL[c.id] ?? <Radio size={26} />}
      </Ladrilho>
      <h2>{c.nome}</h2>
      <p>{c.oQueE}</p>
      <div className="hugi-conectar-ladrilho-pe">
        {pronto ? (
          <button type="button" className="v2-botao is-primario" onClick={props.aoEscolher}>
            Conectar
          </button>
        ) : (
          <Selo tom={selo.tom}>{selo.rotulo}</Selo>
        )}
      </div>
    </article>
  );
}

/* ============================== WhatsApp por QR Code ============================== */

/**
 * O pareamento por QR — o passo que era um comando de terminal (H64).
 *
 * ## O que a tela diz ANTES de mostrar o código
 *
 * As três coisas que decidem se a pessoa vai até o fim, e que ela não tem como adivinhar: que o número
 * continua funcionando no celular dela; que o WhatsApp Web dela não cai; e onde exatamente fica o menu
 * que ela precisa abrir. A terceira é a que mais trava — "leia o QR" pressupõe saber que existe
 * *Aparelhos conectados*.
 *
 * ## Por que o código aparece só depois do clique
 *
 * Porque abrir o pareamento **abre um socket** contra o WhatsApp do outro lado e cria um dispositivo no
 * banco. Fazer isso ao abrir a tela gastaria os dois em toda visita de quem só estava olhando — e cada
 * código dura ~20 segundos.
 */
function WhatsAppPorQr(props: { readonly aoPreferirOficial: () => void }) {
  const cliente = useQueryClient();
  const [id, setId] = useState<string | null>(null);
  const [pedindo, setPedindo] = useState(false);
  const [erroAoPedir, setErroAoPedir] = useState<string | null>(null);

  /*
   * O pareamento em aberto é cancelado quando a tela sai.
   *
   * Sem isto, sair da tela deixa lá fora um socket contra o WhatsApp e um dispositivo pela metade no
   * banco, até o tempo acabar. O `ref` existe porque a limpeza roda no desmonte, quando o `useState` já
   * não vale.
   */
  const emAberto = useRef<string | null>(null);
  useEffect(() => {
    return () => {
      const aberto = emAberto.current;
      if (aberto) void cancelarPareamento(cliente, aberto);
    };
  }, [cliente]);

  const pareamento = usePareamento(id);
  const [gerandoLink, setGerandoLink] = useState(false);
  const [link, setLink] = useState<{ url: string; expiraEm: string } | null>(null);
  const estado = pareamento.data?.estado ?? (id ? "abrindo" : null);

  // Pareou: não há mais o que cancelar, e cancelar depois do sucesso apagaria um dispositivo que agora
  // é a conexão do cliente.
  if (estado === "pareado" || estado === "falhou") emAberto.current = null;

  async function gerarLink() {
    setGerandoLink(true);
    setErroAoPedir(null);
    const r = await emitirLinkDePareamento();
    setGerandoLink(false);
    if (!r.ok) {
      setErroAoPedir(r.falha?.erro.mensagem ?? "Não deu para gerar o link agora. Tente de novo em alguns instantes.");
      return;
    }
    setLink({ url: r.url, expiraEm: r.expiraEm });
  }

  async function comecar() {
    setPedindo(true);
    setErroAoPedir(null);
    const r = await pedirPareamento();
    setPedindo(false);
    if (!r.ok) {
      setErroAoPedir(r.falha?.erro.mensagem ?? "Não deu para começar o pareamento agora. Tente de novo em alguns instantes.");
      return;
    }
    emAberto.current = r.pareamentoId;
    setId(r.pareamentoId);
  }

  function recomecar() {
    const aberto = emAberto.current;
    if (aberto) void cancelarPareamento(cliente, aberto);
    emAberto.current = null;
    setId(null);
    void comecar();
  }

  if (estado === "pareado") {
    return (
      <>
        <div className="hugi-conectar-linha">
          <h2>Conectado{pareamento.data?.numero ? ` — ${identificadorLegivel(pareamento.data.numero)}` : ""}</h2>
          <Selo tom="sucesso">Recebendo</Selo>
        </div>
        <p className="hugi-conectar-fraco">
          A partir de agora, tudo que chegar neste número aparece na caixa de entrada da sua equipe. O WhatsApp continua
          funcionando normalmente no seu celular.
        </p>
        <ProximoPasso />
      </>
    );
  }

  return (
    <>
      <div>
        <h2>Conectar o WhatsApp que você já usa</h2>
        <p className="hugi-conectar-fraco">O Hugi entra como mais um aparelho conectado, do mesmo jeito que o WhatsApp Web.</p>
      </div>

      <ul className="hugi-conectar-avisos">
        <li>Você continua usando o WhatsApp no celular, com as mesmas conversas.</li>
        <li>O WhatsApp Web que você já tem aberto não cai — cabem vários aparelhos.</li>
        <li>Tenha o celular na mão: o código vale por alguns segundos e se renova sozinho.</li>
      </ul>

      {id === null ? (
        <>
          <div className="hugi-conectar-botoes">
            <button type="button" className="v2-botao is-primario" onClick={() => void comecar()} disabled={pedindo}>
              {pedindo ? "Preparando…" : "Mostrar o código"}
            </button>
            {/*
              O SEGUNDO caminho, e ele não é um detalhe: o de cima só serve a quem tem DUAS telas.
              Ninguém fotografa o próprio celular — quem abriu o painel no mesmo aparelho que vai
              conectar fica sem saída sem este botão.
            */}
            <button type="button" className="v2-botao" onClick={() => void gerarLink()} disabled={gerandoLink}>
              {gerandoLink ? "Gerando…" : "Conectar neste celular"}
            </button>
          </div>

          {link ? <LinkDeConexao url={link.url} expiraEm={link.expiraEm} /> : null}

          {erroAoPedir ? <p role="alert" className="v2-erro">{erroAoPedir}</p> : null}

          {/* O oficial num link pequeno (spec 23): a API Hugi é o caminho do plano, o oficial é a exceção. */}
          <p className="hugi-conectar-fraco hugi-conectar-miudo">
            <button type="button" className="v2-link hugi-conectar-link-oficial" onClick={props.aoPreferirOficial}>
              Prefere a API oficial da Meta?
            </button>
          </p>
        </>
      ) : (
        <Codigo estado={estado} qrSvg={pareamento.data?.qrSvg} explicacao={pareamento.data?.explicacao} aoRecomecar={recomecar} />
      )}
    </>
  );
}

/**
 * O LINK para conectar noutro aparelho — ou neste mesmo, vindo do painel aberto no computador.
 *
 * Ele é mostrado, e não só copiado ("Copiado!" sem o endereço à vista deixa a pessoa sem alternativa
 * quando a cópia falha); diz que vale UMA vez e por pouco tempo; e o token está no fragmento (`#t=…`),
 * que não vai ao servidor nem a log nem a `Referer`.
 */
/** "em 28 minutos" / "em menos de um minuto". Nada de segundos: ninguém corre contra este relógio. */
function minutosAte(iso: string): string {
  const restam = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (!Number.isFinite(restam) || restam <= 0) return "em instantes";
  return restam === 1 ? "em 1 minuto" : `em ${restam} minutos`;
}

function LinkDeConexao(props: { url: string; expiraEm: string }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(props.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sem área de transferência, o endereço à vista continua servindo: dá para selecionar à mão.
      setCopiado(false);
    }
  }

  return (
    <div className="v2-cartao hugi-conectar-link">
      {/*
        Os dois casos, na mesma frase: quem abriu o painel NO celular que vai atender só precisa
        tocar; quem está no computador precisa levar o endereço para o telefone.
      */}
      <p>Toque para conectar este aparelho — ou abra o endereço no celular que vai atender:</p>
      {/*
        O PRÓPRIO endereço é o link: ele é gerado com frequência no próprio celular que vai ser
        conectado, e ali "copie e cole" é uma etapa sem motivo. `target="_blank"` porque o painel fica
        aberto, e `rel="noopener"` é obrigatório com ele.
      */}
      <a href={props.url} target="_blank" rel="noopener noreferrer" className="hugi-conectar-endereco">
        {props.url}
      </a>
      <div className="hugi-conectar-botoes">
        <button type="button" className="v2-botao is-primario" onClick={() => window.open(props.url, "_blank", "noopener,noreferrer")}>
          Abrir agora
        </button>
        <button type="button" className="v2-botao" onClick={() => void copiar()}>
          {copiado ? "Copiado" : "Copiar o link"}
        </button>
        {/* O prazo REAL, e não "poucos minutos": o servidor sabe a hora exata. */}
        <span className="hugi-conectar-fraco hugi-conectar-miudo">Vale uma vez só, e expira {minutosAte(props.expiraEm)}.</span>
      </div>
    </div>
  );
}

/**
 * O código, e as instruções ao lado dele.
 *
 * As instruções ficam **ao lado** e não abaixo: quem está com o celular na mão olha o código e o menu ao
 * mesmo tempo, e um passo a passo abaixo da imagem sai da tela no celular exatamente quando é preciso.
 */
function Codigo(props: {
  readonly estado: string | null;
  readonly qrSvg: string | undefined;
  readonly explicacao: string | undefined;
  readonly aoRecomecar: () => void;
}) {
  if (props.estado === "falhou") {
    return (
      <div className="hugi-conectar-falhou">
        <p role="alert">{props.explicacao ?? "O pareamento não foi concluído."}</p>
        <div>
          <button type="button" className="v2-botao is-primario" onClick={props.aoRecomecar}>Gerar outro código</button>
        </div>
      </div>
    );
  }

  return (
    <div className="hugi-conectar-codigo">
      {/* `max-width: 100%`: numa coluna estreita o quadrado de 224px vazava para fora do cartão. */}
      <div className="hugi-conectar-qr">
        {props.qrSvg ? (
          <div
            // O SVG vem do NOSSO servidor, desenhado pela biblioteca de QR a partir do código do
            // WhatsApp — não é conteúdo de terceiro passando por aqui.
            dangerouslySetInnerHTML={{ __html: props.qrSvg }}
            aria-label="Código QR para conectar o WhatsApp"
            role="img"
            className="hugi-conectar-qr-imagem"
          />
        ) : (
          /*
            Esqueleto com a FORMA do código, e não um spinner: o quadrado no lugar certo evita o salto
            de layout no instante em que ele chega — que é o instante em que a pessoa está mirando a
            câmera. Com borda, senão o esqueleto some sobre o cartão.
          */
          <div className="hugi-conectar-qr-esqueleto" />
        )}
      </div>

      <div className="hugi-conectar-instrucoes">
        <ol>
          <li>Abra o WhatsApp no celular.</li>
          <li>
            Toque em <strong>Configurações</strong> e depois em <strong>Aparelhos conectados</strong>.
            {/* No Android o menu é o dos três pontos; dizer só "Configurações" manda metade das
                pessoas procurar no lugar errado. */}
            <span className="hugi-conectar-fraco hugi-conectar-bloco">No Android, o menu dos três pontos no canto.</span>
          </li>
          <li>
            Toque em <strong>Conectar aparelho</strong> e aponte a câmera para o código ao lado.
          </li>
        </ol>

        <p className="hugi-conectar-fraco hugi-conectar-miudo">
          {props.qrSvg
            ? "O código se renova sozinho a cada poucos segundos. Não precisa fazer nada — quando ele mudar na tela, aponte de novo."
            : "Falando com o WhatsApp…"}
        </p>

        <div>
          <button type="button" className="v2-botao" onClick={props.aoRecomecar}>Gerar outro código</button>
        </div>
      </div>
    </div>
  );
}

/**
 * O PRÓXIMO PASSO depois de conectar (spec 23, jornada 1): o número ligado ainda não responde sozinho — falta dizer
 * QUANDO o assistente responde. O primário leva ao assistente principal, já na aba certa; o atendimento e a lista
 * de canais ficam como secundários.
 *
 * O id vem da lista de agentes (o principal é o mais antigo vivo, como o servidor decide). Sem ela, cai na área de
 * Automações — o botão nunca fica sem destino.
 */
function ProximoPasso() {
  const [, navegar] = useLocation();
  const agentes = useAgentes();
  const lista = agentes.data?.agentes ?? [];
  const principal = lista.find((a) => a.principal) ?? lista[0] ?? null;
  const destino = principal ? `/automacoes/assistente/${principal.id}?aba=quando` : "/automacoes";
  return (
    <div className="hugi-conectar-botoes">
      <button type="button" className="v2-botao is-primario" onClick={() => navegar(destino)}>
        Escolher quando ele responde
      </button>
      <button
        type="button"
        className="v2-botao"
        onClick={() => {
          // O inbox é OUTRO build, na mesma origem (§3 da spec 02) — navegação de verdade, e não
          // `Link` do wouter, que só troca a rota deste bundle.
          window.location.href = "/atender";
        }}
      >
        Abrir o atendimento
      </button>
      <button type="button" className="v2-botao" onClick={() => navegar("/canais")}>Ver meus canais</button>
    </div>
  );
}

/* ============================== Telegram ============================== */

/**
 * Conectar um bot de Telegram — §3.1 da spec 07.
 *
 * Os passos do @BotFather em ordem, **com o que copiar e colar**: quem nunca falou com um bot não sabe
 * que `@BotFather` é um contato que se procura na busca.
 *
 * ## O que esta seção NÃO tem
 *
 * **Campo "digite o número".** No Telegram o `chat_id` só existe depois que a pessoa manda `/start`, e
 * prometer abordagem ativa é prometer o que o canal não faz. O que a tela entrega no fim é **o link do
 * bot**, para o cliente divulgar.
 */
function Telegram() {
  const cliente = useQueryClient();
  const [, navegar] = useLocation();
  const [token, setToken] = useState("");
  const [conectando, setConectando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState<TelegramConectado | null>(null);
  const idDoMotivo = useId();

  async function conectar() {
    setConectando(true);
    setErro(null);
    const r = await conectarTelegram(cliente, token.trim());
    setConectando(false);
    if (r.ok && r.dados) {
      setPronto(r.dados);
      // O token sai da memória da tela assim que foi guardado. Não é criptografia — é não deixar
      // credencial num campo de formulário que alguém pode ter aberto ao lado de outra pessoa.
      setToken("");
      return;
    }
    setErro(r.falha?.erro.mensagem ?? "Não foi possível conectar.");
  }

  if (pronto) {
    return (
      <>
        <div className="hugi-conectar-linha">
          <h2>@{pronto.bot} está conectado</h2>
          <Selo tom="sucesso">Recebendo</Selo>
        </div>
        <p className="hugi-conectar-fraco">
          Divulgue este link no site, na bio e onde mais fizer sentido. No Telegram a conversa só começa quando a pessoa
          escreve — não há como o bot falar primeiro.
        </p>
        <code className="hugi-conectar-endereco">{pronto.link}</code>

        {/*
          Se o `setWebhook` falhou, a conexão EXISTE e ainda não recebe. Dizer isso é a diferença entre
          "está configurando" e "não funciona".
        */}
        {pronto.webhookRegistrado ? (
          <p className="hugi-conectar-ok hugi-conectar-miudo">O Telegram já sabe para onde mandar as mensagens.</p>
        ) : (
          <p role="alert" className="hugi-conectar-atencao hugi-conectar-miudo">{pronto.avisoDoWebhook}</p>
        )}

        <div className="hugi-conectar-botoes">
          <button type="button" className="v2-botao is-primario" onClick={() => void navigator.clipboard?.writeText(pronto.link)}>
            Copiar link
          </button>
          <button type="button" className="v2-botao" onClick={() => navegar("/canais")}>Ver meus canais</button>
        </div>
      </>
    );
  }

  const motivo = token.trim() ? null : "Cole a chave do bot para conectar.";

  return (
    <>
      <div>
        <h2>Criar o bot da sua empresa</h2>
        <p className="hugi-conectar-fraco">
          Quem cria o bot é você, dentro do Telegram. O Hugi só precisa da chave que ele te devolve no fim.
        </p>
      </div>

      <ol className="hugi-conectar-passos">
        <li>
          No Telegram, procure por <strong>@BotFather</strong> — é um contato oficial, com selo azul — e abra a conversa.
        </li>
        <li>
          Mande <code>/newbot</code>.
        </li>
        <li>
          Ele pergunta o <strong>nome</strong> (o que aparece na conversa, pode ter espaço e acento) e depois o{" "}
          <strong>usuário</strong>, que precisa terminar em <code>bot</code> — por exemplo <code>clinica_exemplo_bot</code>.
        </li>
        <li>
          Ele responde com uma linha começando por <em>Use this token…</em>. Copie o código e cole abaixo.
        </li>
      </ol>

      <div className="v2-cadastro-campo hugi-conectar-campo">
        <label htmlFor={`${idDoMotivo}-token`}>Chave do bot</label>
        <input
          id={`${idDoMotivo}-token`}
          // `password` para não ficar legível na tela: quem cola isto pode estar compartilhando a tela.
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="123456789:AAExemplo-de-chave"
          className="campo hugi-conectar-token"
          aria-describedby={`${idDoMotivo}-ajuda`}
        />
        <small id={`${idDoMotivo}-ajuda`} className="v2-cadastro-ajuda">
          Ela fica guardada cifrada no servidor e não volta para esta tela nunca mais.
        </small>
      </div>

      <div className="hugi-conectar-botoes">
        <button type="button" className="v2-botao is-primario" onClick={() => void conectar()} disabled={conectando || motivo !== null}
          {...(motivo ? { "aria-describedby": idDoMotivo } : {})}>
          {conectando ? "Conectando…" : "Conectar"}
        </button>
        {motivo ? <Motivo id={idDoMotivo}>{motivo}</Motivo> : null}
      </div>

      {erro ? <p role="alert" className="v2-erro">{erro}</p> : null}

      <div className="hugi-conectar-fraco hugi-conectar-miudo">
        <p className="hugi-conectar-forte">Duas coisas antes de divulgar:</p>
        <ul className="hugi-conectar-avisos">
          <li>A conversa não começa sozinha — quem escreve primeiro é sempre a pessoa.</li>
          <li>
            Ela pode bloquear o bot quando quiser. Isso chega como erro de envio e marca o contato como bloqueado; o Hugi não
            fica tentando para sempre.
          </li>
        </ul>
      </div>
    </>
  );
}
